import { useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../lib/api';
import { Button } from '../components/ui/Button';
import { EmptyState } from '../components/ui/EmptyState';
import { VentesFilters, type VentesFiltersValue } from '../components/ventes/VentesFilters';
import { VentesTable } from '../components/ventes/VentesTable';

const EMPTY: VentesFiltersValue = {
  period_from: '',
  period_to: '',
  user_id: '',
  statut_paiement: '',
  type_client: '',
};

export function VentesPage(): JSX.Element {
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: VentesFiltersValue = useMemo(
    () => ({
      period_from: searchParams.get('period_from') ?? '',
      period_to: searchParams.get('period_to') ?? '',
      user_id: searchParams.get('user_id') ?? '',
      statut_paiement: searchParams.get('statut_paiement') ?? '',
      type_client: searchParams.get('type_client') ?? '',
    }),
    [searchParams],
  );

  const page = Number.parseInt(searchParams.get('page') ?? '1', 10);

  const query = useQuery({
    queryKey: ['ventes', filters, page],
    queryFn: () =>
      api.ventes.list({
        period_from:
          filters.period_from === '' ? undefined : `${filters.period_from}T00:00:00.000Z`,
        period_to: filters.period_to === '' ? undefined : `${filters.period_to}T23:59:59.999Z`,
        user_id: filters.user_id === '' ? undefined : Number(filters.user_id),
        statut_paiement: filters.statut_paiement === '' ? undefined : filters.statut_paiement,
        type_client: filters.type_client === '' ? undefined : filters.type_client,
        page,
        page_size: 25,
      }),
  });

  const updateFilters = (v: VentesFiltersValue): void => {
    const next = new URLSearchParams();
    for (const [k, val] of Object.entries(v)) {
      if (val !== '') next.set(k, val);
    }
    setSearchParams(next);
  };

  const total = query.data?.total ?? 0;
  const pageSize = query.data?.page_size ?? 25;
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const goToPage = (p: number): void => {
    const next = new URLSearchParams(searchParams);
    next.set('page', String(p));
    setSearchParams(next);
  };

  return (
    <div className="max-w-6xl">
      <header className="flex items-end justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Ventes</h1>
          <p className="text-xs text-slate-500 mt-1">
            Liste des ventes saisies. Tri date décroissante.
          </p>
        </div>
        <Link to="/ventes/nouveau">
          <Button>+ Nouvelle vente</Button>
        </Link>
      </header>

      <div className="mb-4">
        <VentesFilters
          value={filters}
          onChange={updateFilters}
          onReset={(): void => updateFilters(EMPTY)}
        />
      </div>

      {query.isLoading && <div className="text-sm text-slate-400">Chargement…</div>}
      {query.isError && <div className="text-sm text-red-400">Erreur de chargement.</div>}

      {query.isSuccess && query.data.items.length === 0 && (
        <EmptyState
          title="Aucune vente sur ces critères."
          description="Ajustez les filtres ou créez une nouvelle vente."
          action={
            <Link to="/ventes/nouveau">
              <Button>+ Nouvelle vente</Button>
            </Link>
          }
        />
      )}

      {query.isSuccess && query.data.items.length > 0 && (
        <>
          <VentesTable items={query.data.items} />
          <div className="mt-4 flex items-center justify-between text-xs text-slate-400">
            <div>
              {total} vente{total > 1 ? 's' : ''} — page {page} / {totalPages}
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={(): void => goToPage(page - 1)}
              >
                Précédent
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={page >= totalPages}
                onClick={(): void => goToPage(page + 1)}
              >
                Suivant
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
